import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ActionPreviewCardProps {
  actionId: string;
  summary: string;
  affectedCount?: number;
  warnings?: string[];
  destructive?: boolean;
  entityName?: string;
  onConfirm: (typedName?: string) => void;
  onCancel: () => void;
  onEdit?: () => void;
  isLoading?: boolean;
}

export function ActionPreviewCard(props: ActionPreviewCardProps) {
  const { t } = useTranslation('console-ai');
  const [typed, setTyped] = useState('');
  const canConfirm = props.destructive ? typed === props.entityName : true;

  return (
    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
      <div className="flex justify-between items-center mb-2">
        <span className="font-semibold">{props.summary}</span>
        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-mono">{props.actionId}</span>
      </div>

      {typeof props.affectedCount === 'number' && (
        <div className="text-xs opacity-75 mb-1">
          {t('preview.matchCount', { count: props.affectedCount })}
        </div>
      )}

      {props.warnings && props.warnings.length > 0 && (
        <ul className="text-xs text-amber-300 space-y-0.5 mb-2">
          {props.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
        </ul>
      )}

      {props.destructive && props.entityName && (
        <div className="mb-2">
          <label className="block text-xs opacity-70 mb-1">
            {t('preview.destructiveTypeName', { entityType: props.entityName })}
          </label>
          <input
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder={props.entityName}
            className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-sm"
          />
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <button onClick={props.onCancel} disabled={props.isLoading}
                className="flex-1 rounded border border-white/15 bg-white/5 px-3 py-1 text-sm">
          {t('preview.cancel')}
        </button>
        {props.onEdit && (
          <button onClick={props.onEdit} disabled={props.isLoading}
                  className="flex-1 rounded border border-white/15 bg-white/5 px-3 py-1 text-sm">
            {t('preview.edit')}
          </button>
        )}
        <button
          onClick={() => props.onConfirm(props.destructive ? typed : undefined)}
          disabled={!canConfirm || props.isLoading}
          className={`flex-1 rounded px-3 py-1 text-sm font-semibold ${
            props.destructive
              ? 'border border-rose-500/60 bg-rose-500/15 text-rose-200 disabled:opacity-50'
              : 'border border-emerald-500/60 bg-emerald-500/20 text-emerald-200 disabled:opacity-50'
          }`}
        >
          {t('preview.confirm')}
        </button>
      </div>
    </div>
  );
}
